import { profilePnL, formatMoney, formatPct } from '../model/engine';

const PLAN_OPTIONS = ['free', 'basic', 'premium', 'enterprise'];

function statusClass(status) {
  if (status === 'healthy') return 'economics-pill economics-pill-healthy';
  if (status === 'ok') return 'economics-pill economics-pill-ok';
  if (status === 'thin') return 'economics-pill economics-pill-thin';
  if (status === 'danger') return 'economics-pill economics-pill-danger';
  return 'economics-pill';
}

function statusLabel(status) {
  if (status === 'not_monetized') return 'Not monetized';
  if (status === 'danger') return 'Underwater';
  if (status === 'thin') return 'Thin';
  if (status === 'ok') return 'OK';
  return 'Healthy';
}

function newProfileId() {
  return `profile-${Date.now().toString(36)}`;
}

export default function ProfilesPanel({ profiles, plans, rates, onChange }) {
  const updateProfile = (id, patch) => {
    onChange(profiles.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const removeProfile = (id) => {
    onChange(profiles.filter((p) => p.id !== id));
  };

  const addProfile = () => {
    onChange([
      ...profiles,
      {
        id: newProfileId(),
        name: 'New profile',
        plan: 'basic',
        items: 2,
        books: 1,
        balanceWaves: 10,
        txRefreshCallsMo: 0,
        authEnabled: false,
        aiTurnsMo: 0,
        addOns: {},
        note: '',
      },
    ]);
  };

  const toggleAddOn = (id, key) => {
    const profile = profiles.find((p) => p.id === id);
    if (!profile) return;
    if (key === 'authOnce') {
      updateProfile(id, { authEnabled: !profile.authEnabled });
      return;
    }
    const addOns = { ...(profile.addOns || {}) };
    if (key === 'enrichThousandTx') {
      addOns.enrichThousandTx = addOns.enrichThousandTx ? 0 : 1000;
    } else {
      addOns[key] = !addOns[key];
    }
    updateProfile(id, { addOns });
  };

  return (
    <section className="chart-card economics-panel">
      <div className="economics-panel-header">
        <div>
          <h2>Usage profiles</h2>
          <p className="chart-hint">
            Per-customer Plaid + Stripe + AI. Net GM is recurring monthly: plan + AI packs − Plaid −
            Stripe − AI COGS. Free/Basic: no AI. Premium+: included quota; turns above included buy
            packs. Auth is an optional one-time cost; transaction refreshes are recurring calls.
          </p>
        </div>
        <button type="button" className="secondary-button" onClick={addProfile}>
          Add profile
        </button>
      </div>
      <div className="table-scroll">
        <table className="data-table economics-profiles-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Plan</th>
              <th>Books</th>
              <th>Items</th>
              <th>Waves/mo</th>
              <th>Tx refreshes/mo</th>
              <th>AI turns/mo</th>
              <th>Add-ons</th>
              <th>Revenue</th>
              <th>AI packs</th>
              <th>COGS</th>
              <th>Auth once</th>
              <th>Stripe</th>
              <th>AI</th>
              <th>Net GM</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => {
              const pnl = profilePnL(profile, plans, rates);
              const aiEligible = profile.plan === 'premium' || profile.plan === 'enterprise';
              return (
                <tr key={profile.id}>
                  <td>
                    <input
                      className="economics-inline-input economics-inline-input-wide"
                      value={profile.name}
                      onChange={(e) => updateProfile(profile.id, { name: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      value={profile.plan}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          plan: e.target.value,
                          ...(plans[e.target.value]?.bankLink ? {} : { authEnabled: false }),
                        })
                      }
                    >
                      {PLAN_OPTIONS.map((slug) => (
                        <option key={slug} value={slug}>
                          {plans[slug]?.name || slug}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="1"
                      value={profile.books ?? 1}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          books: Math.max(1, parseInt(e.target.value, 10) || 1),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="0"
                      value={profile.items}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          items: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="0"
                      value={profile.balanceWaves}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          balanceWaves: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="0"
                      value={profile.txRefreshCallsMo ?? 0}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          txRefreshCallsMo: Math.max(
                            0,
                            parseInt(e.target.value, 10) || 0
                          ),
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="economics-inline-input"
                      type="number"
                      min="0"
                      value={profile.aiTurnsMo ?? 0}
                      onChange={(e) =>
                        updateProfile(profile.id, {
                          aiTurnsMo: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                      disabled={!aiEligible}
                      title={aiEligible ? 'AI turns / mo' : 'Premium+ only'}
                    />
                  </td>
                  <td className="economics-addons-cell">
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(profile.addOns?.recurring)}
                        onChange={() => toggleAddOn(profile.id, 'recurring')}
                      />
                      Rec
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(profile.addOns?.liabilities)}
                        onChange={() => toggleAddOn(profile.id, 'liabilities')}
                      />
                      Liab
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(profile.addOns?.investments)}
                        onChange={() => toggleAddOn(profile.id, 'investments')}
                      />
                      Inv
                    </label>
                    <label title="Optional one-time Auth product cost; off by default per PCC-8">
                      <input
                        type="checkbox"
                        checked={Boolean(profile.authEnabled)}
                        onChange={() => toggleAddOn(profile.id, 'authOnce')}
                        disabled={!plans[profile.plan]?.bankLink}
                      />
                      Auth
                    </label>
                  </td>
                  <td>{formatMoney(pnl.revenue)}</td>
                  <td>
                    {pnl.aiPacks > 0
                      ? `${pnl.aiPacks} · ${formatMoney(pnl.aiPackRevenue)}`
                      : '—'}
                  </td>
                  <td>{formatMoney(pnl.cogs)}</td>
                  <td>{pnl.authCogs > 0 ? formatMoney(pnl.authCogs) : '—'}</td>
                  <td>{formatMoney(pnl.stripe)}</td>
                  <td>{formatMoney(pnl.ai || 0)}</td>
                  <td>
                    {formatMoney(pnl.gmNet)}{' '}
                    <span className="economics-muted">({formatPct(pnl.gmNetPct)})</span>
                  </td>
                  <td>
                    <span className={statusClass(pnl.status)}>{statusLabel(pnl.status)}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => removeProfile(profile.id)}
                      disabled={profiles.length <= 1}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
